import React, { useMemo, useState } from 'react';
import { CheckSquare2, Calendar, Radio, Home, Briefcase, Phone, Mail, Zap, ChevronRight, ChevronDown } from 'lucide-react';
import type { TimelineEntry, TimelineThreadMessage } from '@shared/schemas/timeline-entry';
import type { EntitySignalRow } from '@shared/flow/entity-signals';
import { sfdcRecordUrl, CompactUrlLink, SfdcCloudIcon, SourceLinkIcon, SourceOpenButton, type SfdcRecordKind } from '../links';
import { useFlowAccountTimeline, useFlowAccountSignals } from './hooks';
import { hasActionAffordance } from '../actions/ActionsTabList';
import { actionFields, isLiveActionRow, type DetailField } from '../actions/action-fields';
import { WorkflowsDropdown } from '../contact/ContactActionMenus';
import {
  type AccountEntryKind,
  type FilterGroup,
  type Mode,
  type TimelineRow,
  GROUP_LABEL,
  bucketLabel,
  bucketOrder,
  fmtDay,
  actionBucket,
  entryTitle,
  entryMetaText,
  entryFields,
  entryDescription,
  workspaceIdOf,
  ACCOUNT_TIMELINE_TRUNCATED_COPY,
  countByGroup,
  buildTimelineRows,
  visibleRows,
  bucketize,
  allExpanded,
  toggleExpandAll,
  syncedRowsBadge,
  threadBadge,
} from './accountTimelineRows';

// Re-exported so `AccountSignalsPanel.tsx:4`'s `import { ... } from
// './AccountTimeline'` keeps resolving unchanged (U1e lift, plan 005 Wave 0
// — the row/bucket logic moved to `accountTimelineRows.ts`, this file keeps
// re-exporting the pure helpers it and AccountSignalsPanel both need).
export { bucketLabel, bucketOrder, fmtDay, actionBucket, entryTitle, entryMetaText };

/**
 * Account timeline for the drill-down — renders the core account-timeline
 * endpoint's entries grouped by their SERVER-computed bucket ('upcoming',
 * 'YYYY-MM', 'undated'). Do not re-derive dates client-side for WIRE entries —
 * the server is the bucketing authority (explicit contract in
 * src/api/account-timeline.ts).
 *
 * Terra action rows (`useFlowAccountSignals`) MERGE into that same
 * chronological accordion (U6/R16, 2026-09-23 — the account twin of
 * `ContactTimeline`'s same change): they are ordinary accordion rows, not a
 * separate `ActionsTabList` view, and the Actions chip is just a filter over
 * the merged list. They are the ONE exception to the no-client-bucketing rule
 * — a `terra_signals` row never passes through the timeline endpoint, so it
 * carries no server `bucket`; `actionBucket` below mirrors the server's own
 * rule for them (see its pointer comment). The contract note in
 * `src/api/account-timeline.ts` is about the WIRE entries it serves, which are
 * still rendered by their server bucket, untouched.
 *
 * Rich-row treatment (GTMOPS-2162, Bucket B U6): kind icon tile + chevron +
 * in-place expand with per-kind detail fields + a "Open in Salesforce ↗"
 * button on every expanded row. Matches the prototype's `tlRow`
 * (docs/flow/command-ux-bucket-b-prototype.html :871-899) — deliberately
 * WITHOUT the prototype's separate `renderSignals` view (:902-925), which
 * predates the Option B server-side interleave decision (Decision 2): the
 * backend already merges tasks/events/signals into one sorted stream, so
 * Signals here is just a third filter chip over the SAME stream, never a
 * client-side merge of a separate `/api/accounts/:id/signals` endpoint.
 *
 * Workspace creates + joins (Bucket B U9): two wire kinds
 * (`workspace_created` / `workspace_join`) surface under ONE "Workspace"
 * filter pill — the prototype's matcha `⌂` tile
 * (docs/flow/command-ux-bucket-b-prototype.html :182). The EXPANDED detail
 * for these two kinds still links out to the Clay app workspace itself
 * (`WorkspaceOpenLink`) rather than an "Open in Salesforce" button — that
 * decision stands. But `entry.id` IS a real SFDC record id for every kind,
 * including these two (`Workspace__c` / `Workspace_User_Relationship__c` —
 * see `timeline-entry.ts`'s doc comments and
 * `src/services/account-activities/seams.ts`'s mapper comments), so the
 * COLLAPSED row's Salesforce cloud out-link icon (GTMOPS-2162 follow-up —
 * same treatment as the Opps/Signals tabs) covers all five kinds via
 * `KIND_CONFIG[...].sfdcCloudKind`.
 */

/** Active-filter pill background per group — signals get the prototype's
 *  purple `pill.sig.on` accent (ube-600), workspace gets the prototype's
 *  matcha accent; task/event/action share the default content-primary. */
const GROUP_PILL_ON_CLASS: Record<Mode, string> = {
  task: 'bg-content-primary border-content-primary dark:bg-nightshade-100 dark:border-nightshade-100',
  event: 'bg-content-primary border-content-primary dark:bg-nightshade-100 dark:border-nightshade-100',
  signal: 'bg-ube-600 border-ube-600',
  workspace: 'bg-matcha-600 border-matcha-600',
  action: 'bg-content-primary border-content-primary dark:bg-nightshade-100 dark:border-nightshade-100',
};

export interface KindConfig {
  Icon: typeof CheckSquare2;
  tileClass: string;
  /** `sfdcRecordUrl` kind for this entry's EXPANDED "Open in Salesforce"
   *  button. `null` for kinds with no SFDC record link there (workspace
   *  rows use `WorkspaceOpenLink` instead — see the header comment). */
  sfdcKind: 'Task' | 'Event' | 'Events_and_Signals__c' | null;
  /** `sfdcRecordUrl` kind for the COLLAPSED row's Salesforce cloud out-link
   *  icon (GTMOPS-2162 follow-up — same treatment as the Opps/Signals
   *  tabs). Unlike `sfdcKind` above, this is never null: `entry.id` is a
   *  real SFDC record id for every kind, including the two workspace kinds. */
  sfdcCloudKind: SfdcRecordKind;
}

export const KIND_CONFIG: Record<AccountEntryKind, KindConfig> = {
  task: {
    Icon: CheckSquare2,
    tileClass: 'bg-tangerine-50 text-tangerine-700 dark:bg-tangerine-900/30 dark:text-tangerine-400',
    sfdcKind: 'Task',
    sfdcCloudKind: 'Task',
  },
  event: {
    Icon: Calendar,
    tileClass: 'bg-nightshade-100 text-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-300',
    sfdcKind: 'Event',
    sfdcCloudKind: 'Event',
  },
  signal: {
    Icon: Radio,
    tileClass: 'bg-ube-50 text-ube-700 dark:bg-ube-900/30 dark:text-ube-400',
    sfdcKind: 'Events_and_Signals__c',
    sfdcCloudKind: 'Events_and_Signals__c',
  },
  workspace_created: {
    Icon: Home,
    tileClass: 'bg-matcha-50 text-matcha-700 dark:bg-matcha-900/30 dark:text-matcha-400',
    sfdcKind: null,
    sfdcCloudKind: 'Workspace__c',
  },
  workspace_join: {
    Icon: Home,
    tileClass: 'bg-matcha-50 text-matcha-700 dark:bg-matcha-900/30 dark:text-matcha-400',
    sfdcKind: null,
    sfdcCloudKind: 'Workspace_User_Relationship__c',
  },
};

/**
 * Standard icon+color tile for Opportunity/deal entries (Bucket B follow-up
 * — Opps-tab row restyle to match the Timeline/Signals row uniform, see
 * `OppRow` in `AccountDetailPanels.tsx`). Defined once here, alongside
 * `KIND_CONFIG`, so any future surface rendering an opportunity row (there is
 * no `opportunity` `AccountEntryKind` today — Opps has its own
 * `OpportunityRow` shape, fetched separately from the timeline/signals
 * stream) imports this rather than re-inventing the icon/color pairing.
 * `lemon` (gold-toned) was chosen because tangerine/nightshade/ube/matcha are
 * already claimed by task/event/signal/workspace, and lemon reads as
 * "deal/money" for a briefcase glyph.
 */
export const OPPORTUNITY_TILE_CONFIG: { Icon: typeof Briefcase; tileClass: string } = {
  Icon: Briefcase,
  tileClass: 'bg-lemon-50 text-lemon-700 dark:bg-lemon-900/30 dark:text-lemon-400',
};

/**
 * Icon+color tile for the Opportunity Activity stream's Call sub-rows
 * (`CallSubRow` in `AccountDetailPanels.tsx`) — same reasoning as
 * `OPPORTUNITY_TILE_CONFIG` above: defined once here alongside `KIND_CONFIG`
 * so the color pairing is discoverable even though today it's only consumed
 * by that one sub-row. `pomegranate` (warm red) was picked over the
 * already-claimed tangerine/nightshade/ube/matcha/lemon set, and reads
 * distinctly from `EMAIL_TILE_CONFIG` below at a glance.
 */
export const CALL_TILE_CONFIG: { Icon: typeof Phone; tileClass: string } = {
  Icon: Phone,
  tileClass: 'bg-pomegranate-50 text-pomegranate-700 dark:bg-pomegranate-900/30 dark:text-pomegranate-400',
};

/**
 * Icon+color tile for the Opportunity Activity stream's Email sub-rows
 * (`EmailSubRow` in `AccountDetailPanels.tsx`). `blueberry` was picked as
 * the last unclaimed brand primitive — distinct from `CALL_TILE_CONFIG`'s
 * pomegranate at a glance.
 */
export const EMAIL_TILE_CONFIG: { Icon: typeof Mail; tileClass: string } = {
  Icon: Mail,
  tileClass: 'bg-blueberry-50 text-blueberry-700 dark:bg-blueberry-900/30 dark:text-blueberry-400',
};

/**
 * Icon+color tile for a merged Terra ACTION row (U6) — the account twin of
 * `ContactTimeline`'s `KIND_DOT.action` (`bg-content-action`). Deliberately
 * built from semantic tokens rather than a brand primitive: tangerine /
 * nightshade / ube / matcha / lemon / pomegranate / blueberry are all already
 * claimed by the tile configs above, and an action row is "the thing Terra
 * wants you to do", which is exactly what `content-action` means.
 */
const ACTION_TILE_CONFIG: { Icon: typeof Zap; tileClass: string } = {
  Icon: Zap,
  tileClass: 'bg-content-action/10 text-content-action dark:bg-blueberry-900/30 dark:text-blueberry-400',
};

/** "Open in Salesforce ↗" button — the prototype's `sfBtn` (:432-435), on
 *  every expanded task/event/signal entry. `stopPropagation` so the click
 *  never re-toggles the row's own trigger button. */
function SfdcOpenButton({ kind, id }: { kind: 'Task' | 'Event' | 'Events_and_Signals__c'; id: string }) {
  return (
    <a
      href={sfdcRecordUrl(kind, id)}
      target="_blank" rel="noreferrer"
      onClick={e => e.stopPropagation()}
      className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-medium text-content-action dark:text-blueberry-400 bg-blueberry-50 dark:bg-blueberry-900/20 border border-blueberry-100 dark:border-blueberry-900/40 rounded-md px-2 py-1 hover:bg-blueberry-100 dark:hover:bg-blueberry-900/30 hover:text-content-action-hover dark:hover:text-blueberry-300"
    >
      Open in Salesforce ↗
    </a>
  );
}

/**
 * "Open workspace ↗" — every workspace row's out-link, built from the real
 * `workspaceId` field (never Name-parsed). Workspace rows have no SFDC
 * record link (see the plan's Deferred item), so this replaces
 * `SfdcOpenButton` for the two workspace kinds. Renders nothing when
 * `workspaceId` is null (nullable upstream, ~100% filled in prod).
 */
function WorkspaceOpenLink({ workspaceId }: { workspaceId: string | null }) {
  if (!workspaceId) return null;
  return (
    <div className="mt-2">
      <CompactUrlLink url={`https://app.clay.com/workspaces/${workspaceId}`} label="Open workspace ↗" />
    </div>
  );
}

/**
 * "×N synced rows" — the Gong fan-out badge (U4, Bucket A/E plan).
 *
 * Gong's Salesforce sync writes ONE Task/Event record per attendee for a
 * single real meeting, all sharing `Gong__Gong_Activity_Id__c`.
 * `buildAccountTimeline` collapses those rows server-side into one entry
 * carrying `syncedRowCount`; this badge is the only place that fact
 * surfaces to the rep. Renders nothing at `syncedRowCount <= 1` (or when
 * the field is absent — pre-U4 wire shape) so an ordinary un-fanned-out row
 * stays clean.
 *
 * Text + tooltip copy are the prototype's verbatim
 * (docs/flow/bucket-a-account-view-prototype.html:877) — do not paraphrase.
 */
function SyncedRowsBadge({ entry }: { entry: Extract<TimelineEntry, { kind: 'task' | 'event' }> }) {
  const badge = syncedRowsBadge(entry);
  if (!badge) return null;
  return (
    <span
      title={badge.title}
      className="inline-flex items-center gap-[3px] shrink-0 text-[10px] font-semibold whitespace-nowrap rounded-[5px] px-1.5 cursor-help text-ube-700 bg-ube-50 border border-ube-100 dark:text-ube-300 dark:bg-ube-900/30 dark:border-ube-900/50"
    >
      ×{badge.count} synced rows
    </span>
  );
}

/**
 * "×N emails · ↑out ↓in" — the collapsed email-thread badge (U8, Bucket A/E
 * plan), at the same altitude as `SyncedRowsBadge` above (same pill shape,
 * same `shrink-0` + `whitespace-nowrap`, same `cursor-help` tooltip), with a
 * distinct accent so a thread never reads as a Gong fan-out. The two are
 * orthogonal and can both appear on one row: `syncedRowCount` counts
 * duplicate SALESFORCE ROWS of one message, `threadCount` counts distinct
 * MESSAGES of a conversation.
 *
 * The arrows are the in/out mix at a glance; the `aria-label` carries the
 * same fact in words, since arrow glyphs are meaningless to a screen reader
 * (and the accessible name is what browser QA's snapshot reads). Renders
 * nothing when `threadCount` is absent — the server only ever emits thread
 * fields on a real ≥ 2 chain, so absence IS "not a thread".
 */
function ThreadCountBadge({ entry }: { entry: Extract<TimelineEntry, { kind: 'task' }> }) {
  const badge = threadBadge(entry);
  if (!badge) return null;
  return (
    <span
      aria-label={badge.ariaLabel}
      title={`${badge.ariaLabel}. Grouped by subject; expand to read each message.`}
      className="inline-flex items-center gap-[3px] shrink-0 text-[10px] font-semibold whitespace-nowrap rounded-[5px] px-1.5 cursor-help text-blueberry-700 bg-blueberry-50 border border-blueberry-100 dark:text-blueberry-300 dark:bg-blueberry-900/30 dark:border-blueberry-900/50"
    >
      ×{badge.count} emails
      {badge.outbound > 0 && <span aria-hidden="true">↑{badge.outbound}</span>}
      {badge.inbound > 0 && <span aria-hidden="true">↓{badge.inbound}</span>}
    </span>
  );
}

/** Per-message direction chip in the expanded thread list. */
function DirectionChip({ direction }: { direction: TimelineThreadMessage['direction'] }) {
  if (!direction) return null;
  const isInbound = direction === 'inbound';
  return (
    <span
      className={`shrink-0 text-[9.5px] font-semibold uppercase tracking-wide rounded-[4px] px-1 ${
        isInbound
          ? 'text-matcha-700 bg-matcha-50 border border-matcha-100 dark:text-matcha-300 dark:bg-matcha-900/30 dark:border-matcha-900/50'
          : 'text-content-tertiary bg-bg-secondary border border-primary/60 dark:text-nightshade-400 dark:bg-nightshade-800 dark:border-nightshade-700'
      }`}
    >
      {isInbound ? 'Received' : 'Sent'}
    </span>
  );
}

/**
 * The expanded thread: every message in wire order (newest-first, per
 * `buildAccountTimeline`'s `threadMessages` contract — this list does NOT
 * re-sort), each its own collapsed sub-row that opens to its body.
 *
 * Auto-unfurl on expand (2026-08-28, Spencer — supersedes the original "one
 * open message at a time" decision below): `ThreadMessageList` remounts
 * fresh every time its parent thread row expands (it only mounts while
 * `isExpanded` is true — see `EntryDetail`), so seeding `openMessageIds` with
 * every message's id at mount time is sufficient to make every nested
 * message body visible the moment the thread opens, while each one stays
 * individually collapsible afterward via its own toggle. Previous behavior:
 * two independent expand levels — the outer row answers "what conversation
 * is this and when did it last move", the inner one "what did this
 * particular email say" — with only one message open at a time (single
 * `openMessageId`), mirroring the outer list's then-single `expandedKey`, so
 * a long thread never became a wall of stacked bodies. The outer list's
 * expand state is now a `Set` too (see `AccountTimeline`'s `expandedKeys`),
 * and reps asked for the same "see everything at once, then trim down"
 * default here.
 *
 * Bodies reuse U7's `whitespace-pre-line` render — email bodies are the
 * single worst offender for the collapsed-newlines bug U7 fixed.
 */
function ThreadMessageList({ messages }: { messages: TimelineThreadMessage[] }) {
  const [openMessageIds, setOpenMessageIds] = useState<Set<string>>(
    () => new Set(messages.map(m => m.id)),
  );
  return (
    <div className="mt-1.5 border-l-2 border-blueberry-100 dark:border-blueberry-900/50 pl-2">
      {messages.map(message => {
        const isOpen = openMessageIds.has(message.id);
        const toggle = () => setOpenMessageIds(prev => {
          const next = new Set(prev);
          if (isOpen) next.delete(message.id); else next.add(message.id);
          return next;
        });
        return (
          <div key={message.id} className="border-b border-primary/30 dark:border-nightshade-800 last:border-b-0">
            <div
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              onClick={e => { e.stopPropagation(); toggle(); }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggle(); }
              }}
              className="w-full flex gap-1.5 items-start py-1 text-[11.5px] text-left cursor-pointer hover:bg-bg-secondary/60 dark:hover:bg-nightshade-800/60 rounded"
            >
              <span className="w-11 shrink-0 text-[11px] text-content-tertiary dark:text-nightshade-500">{fmtDay(message.effectiveAt)}</span>
              <DirectionChip direction={message.direction} />
              {/* RAW subject — prefixes intact, so a rep can see which hop
                  of the chain this is. The stripped form is the row title. */}
              <span className="min-w-0 flex-1 text-content-secondary dark:text-nightshade-300">{message.subject ?? 'Email'}</span>
              {isOpen
                ? <ChevronDown className="w-3 h-3 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />
                : <ChevronRight className="w-3 h-3 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />}
            </div>
            {isOpen && (
              <div className="pl-[52px] pb-1.5">
                {message.description
                  ? <p className="text-[11.5px] leading-relaxed whitespace-pre-line text-content-secondary dark:text-nightshade-300">{message.description}</p>
                  : <div className="text-[11px] italic text-content-tertiary dark:text-nightshade-500">No message body.</div>}
                <SfdcOpenButton kind="Task" id={message.id} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** The label/value rows shared by every expanded body (wire entry OR merged
 *  action row). */
function FieldRows({ detailFields }: { detailFields: DetailField[] }) {
  return (
    <>
      {detailFields.map(f => (
        <div key={f.label} className="flex gap-1.5 text-[11.5px] py-px">
          <span className="w-28 shrink-0 text-content-tertiary dark:text-nightshade-500">{f.label}</span>
          <span className="min-w-0 text-content-secondary dark:text-nightshade-300">{f.value}</span>
        </div>
      ))}
    </>
  );
}

/**
 * Expanded body for a merged ACTION row (U6) — `actionFields` (the same
 * label/value list `ContactTimeline`'s action rows show, shared from
 * `actions/action-fields.ts`) plus the row's Salesforce out-link, rendered at
 * the same indent as `EntryDetail` below so a merged list reads as one thing.
 */
export function ActionDetail({ row }: { row: EntitySignalRow }) {
  const detailFields = actionFields(row);
  return (
    <div className="pl-[74px] pr-2 pb-2.5 pt-0.5">
      <FieldRows detailFields={detailFields} />
      {detailFields.length === 0 && !row.source_link && (
        <div className="text-[11px] italic text-content-tertiary dark:text-nightshade-500">No further detail.</div>
      )}
      <SourceOpenButton url={row.source_link} />
    </div>
  );
}

/** Expanded row body: per-kind field rows + description + out-link. */
export function EntryDetail({ entry }: { entry: TimelineEntry }) {
  // A collapsed thread's body is the MESSAGE LIST, not the latest message's
  // own field rows: Status/Priority/Due describe only the newest email, so
  // showing them as if they described the conversation would misread. The
  // per-message "Open in Salesforce" links live on each member instead of
  // one link to the newest email.
  if (entry.kind === 'task' && entry.threadMessages?.length) {
    return (
      <div className="pl-[74px] pr-2 pb-2.5 pt-0.5">
        <ThreadMessageList messages={entry.threadMessages} />
      </div>
    );
  }
  const detailFields = entryFields(entry);
  const description = entryDescription(entry);
  const isWorkspace = entry.kind === 'workspace_created' || entry.kind === 'workspace_join';
  const { sfdcKind } = KIND_CONFIG[entry.kind];
  return (
    <div className="pl-[74px] pr-2 pb-2.5 pt-0.5">
      <FieldRows detailFields={detailFields} />
      {description
        ? <p className="mt-1.5 text-[11.5px] leading-relaxed whitespace-pre-line text-content-secondary dark:text-nightshade-300">{description}</p>
        : (detailFields.length === 0 && (
            <div className="text-[11px] italic text-content-tertiary dark:text-nightshade-500">No further detail.</div>
          ))}
      {isWorkspace
        ? <WorkspaceOpenLink workspaceId={workspaceIdOf(entry)} />
        : sfdcKind && <SfdcOpenButton kind={sfdcKind} id={entry.id} />}
    </div>
  );
}

export function AccountTimeline({ accountId }: { accountId: string }) {
  const { data, isLoading, error } = useFlowAccountTimeline(accountId);
  // Terra action rows (U6/R16) — a separate fetch boundary from the
  // server-merged timeline above, whose rows MERGE into the same accordion
  // below rather than rendering through a separate mode. A `signalsError`
  // here just means zero action rows merge in; the rest of the timeline still
  // renders (error isolation), so the error is deliberately not destructured
  // — there is no separate Actions surface left to show it on.
  const { data: signalsData, isLoading: signalsLoading } = useFlowAccountSignals(accountId);
  // `signalsData?.rows?.length` (not just `signalsData?.`) — `validateResponse`
  // is observational: a malformed/unmatched-shape response warns and returns
  // the raw payload as-is rather than throwing, which is not guaranteed to
  // carry a `rows` array.
  const actionsCount = signalsData?.rows?.length ?? 0;
  const actionsChipVisible = actionsCount > 0 || signalsLoading;

  const [mode, setMode] = useState<Mode | null>(null);
  // A SET of expanded row keys (2026-08-28, Spencer) — was a single
  // `expandedKey`, so at most one accordion row could ever be open.
  // Individual row clicks are now independently additive: opening row B no
  // longer auto-closes row A. This is a deliberate behavior change, made
  // alongside the new expand-all/collapse-all toggle below.
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const muted = 'text-[12px] text-content-tertiary dark:text-nightshade-500 py-4 text-center';

  // Counts are per PILL GROUP, not per wire kind — `workspace_created` and
  // `workspace_join` both roll up into the single "Workspace" count/pill.
  const counts = useMemo(() => countByGroup(data?.entries ?? []), [data]);

  /**
   * The ONE merged row list: server-bucketed wire entries + client-bucketed
   * action rows (`actionBucket`), sorted `effectiveAt` DESC with undated rows
   * last — the same comparator the server's own merge uses
   * (`src/services/account-timeline.ts`'s `mergeEntries`).
   */
  const rows = useMemo<TimelineRow[]>(
    () => buildTimelineRows(data?.entries ?? [], signalsData?.rows ?? [], new Date()),
    [data, signalsData],
  );

  if (isLoading) return <div className={muted}>Loading timeline…</div>;
  if (error) return <div className="text-[12px] text-content-danger dark:text-red-400 py-4 text-center">Timeline failed to load.</div>;
  if (!data) return null;
  if (!data.hasAccess) return <div className={muted}>No timeline access for this account.</div>;
  // "No activity yet." only when BOTH the timeline AND the Actions read are
  // empty (plan §2b item 5's empty-state fix, mirroring ContactTimeline's).
  // `!signalsLoading` guards against flashing this while the still-in-flight
  // signals fetch simply hasn't landed yet.
  if (data.entries.length === 0 && actionsCount === 0 && !signalsLoading) {
    return <div className={muted}>No activity yet.</div>;
  }

  // Chip semantics mirror the contact side exactly: no chip → the whole
  // merged list; a group chip → that group's wire entries only; the Actions
  // chip → action rows only.
  const visible = visibleRows(rows, mode);

  // Group by bucket, ordered: upcoming → months desc → undated.
  const ordered = bucketize(visible);

  // Expand-all/collapse-all toggle (2026-08-28, Spencer) — scoped to the
  // currently VISIBLE (filtered) rows, mirroring how the pill counts above
  // are also filter-scoped. `isAllExpanded` requires at least one visible
  // row so an empty filter result doesn't read as "already all expanded".
  // Action rows are ordinary accordion rows (U6), so their keys are in scope
  // here like any other.
  const visibleKeys = visible.map(r => r.key);
  const isAllExpanded = allExpanded(expandedKeys, visibleKeys);
  const handleToggleExpandAll = () => setExpandedKeys(prev => toggleExpandAll(prev, visibleKeys));

  return (
    <div>
      {/* Group-filter chips: Tasks · Events · Signals · Workspace — Signals
          gets the prototype's purple `pill.sig.on` accent, Workspace the
          prototype's matcha accent, when active. Workspace is hidden at
          count 0 (create+join both absent), same as every other pill. The
          expand-all/collapse-all toggle sits at the row's end (`ml-auto`). */}
      <div className="flex gap-1 flex-wrap items-center mb-2">
        {(Object.keys(GROUP_LABEL).filter(g => g !== 'action') as FilterGroup[]).filter(g => counts[g]).map(g => {
          const active = mode === g;
          return (
            <button
              key={g}
              onClick={() => setMode(active ? null : g)}
              className={`px-2 py-0.5 rounded-full border text-[10.5px] ${
                active
                  ? `text-white ${GROUP_PILL_ON_CLASS[g]}`
                  : 'bg-white dark:bg-nightshade-800 text-content-secondary dark:text-nightshade-400 border-secondary dark:border-nightshade-700 hover:bg-bg-secondary dark:hover:bg-nightshade-700'
              }`}
            >
              {GROUP_LABEL[g]} ({counts[g]})
            </button>
          );
        })}
        {actionsChipVisible && (
          <button
            onClick={() => setMode(mode === 'action' ? null : 'action')}
            className={`px-2 py-0.5 rounded-full border text-[10.5px] ${
              mode === 'action'
                ? `text-white ${GROUP_PILL_ON_CLASS.action}`
                : 'bg-white dark:bg-nightshade-800 text-content-secondary dark:text-nightshade-400 border-secondary dark:border-nightshade-700 hover:bg-bg-secondary dark:hover:bg-nightshade-700'
            }`}
          >
            {GROUP_LABEL.action}{actionsCount > 0 ? ` (${actionsCount})` : ''}
          </button>
        )}
        <button
          type="button"
          onClick={handleToggleExpandAll}
          aria-label={isAllExpanded ? 'Collapse all' : 'Expand all'}
          className="ml-auto px-2 py-0.5 rounded-full border text-[10.5px] bg-white dark:bg-nightshade-800 text-content-secondary dark:text-nightshade-400 border-secondary dark:border-nightshade-700 hover:bg-bg-secondary dark:hover:bg-nightshade-700"
        >
          {isAllExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      {ordered.map(([bucket, bucketRows]) => (
        <div key={bucket} className="mb-2">
          <div className="sticky top-0 z-[1] bg-white dark:bg-nightshade-900 text-[10.5px] uppercase tracking-wide font-semibold text-content-tertiary dark:text-nightshade-500 py-1">
            {bucketLabel(bucket)}
          </div>
          {bucketRows.map(r => {
            const isExpanded = expandedKeys.has(r.key);
            const toggleRow = () => setExpandedKeys(prev => {
              const next = new Set(prev);
              if (isExpanded) next.delete(r.key); else next.add(r.key);
              return next;
            });
            // Nested controls (the Salesforce cloud icon, an action row's
            // source-link icon or inline WorkflowsDropdown enroll button) own
            // their own activation — Enter/Space bubbling up from one of them
            // must not ALSO toggle the row underneath it (R12).
            const onRowKeyDown = (ev: React.KeyboardEvent) => {
              if (ev.target !== ev.currentTarget) return;
              if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); toggleRow(); }
            };
            if (r.rowKind === 'action') {
              const row = r.signalRow;
              return (
                <div
                  key={r.key}
                  className={`border-b border-primary/40 dark:border-nightshade-800 last:border-b-0 ${
                    isLiveActionRow(row) ? 'bg-tangerine-50 dark:bg-tangerine-900/30' : ''
                  }`}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onClick={toggleRow}
                    onKeyDown={onRowKeyDown}
                    className="w-full flex gap-2 py-1.5 text-[12px] text-left items-start hover:bg-bg-secondary/60 dark:hover:bg-nightshade-800/60 rounded cursor-pointer"
                  >
                    <span className="w-11 shrink-0 text-[11px] text-content-tertiary dark:text-nightshade-500 pt-1">{fmtDay(r.effectiveAt)}</span>
                    <span className={`w-[22px] h-[22px] shrink-0 rounded-md flex items-center justify-center ${ACTION_TILE_CONFIG.tileClass}`}>
                      <ACTION_TILE_CONFIG.Icon className="w-3.5 h-3.5" />
                    </span>
                    <span className="min-w-0 flex-1 pt-px">
                      <span className="font-medium text-content-primary dark:text-nightshade-100">{row.signal_name}</span>
                      {row.action_state && (
                        <span className="text-content-tertiary dark:text-nightshade-500">
                          {' · '}{row.action_state}
                        </span>
                      )}
                    </span>
                    {row.source_link ? (
                      <SourceLinkIcon url={row.source_link} />
                    ) : hasActionAffordance(row) ? (
                      // `contactEmail={null}` is a DOCUMENTED LIMITATION here,
                      // not the U4 bug: `TerraSignalRow` carries no email
                      // column, and at account level there is no single
                      // viewed contact to thread one down from (the contact
                      // timeline passes its own `contactEmail` prop). The
                      // dropdown falls back to its own lookup.
                      <WorkflowsDropdown
                        contactId={row.contact_id!}
                        contactName={row.contact_name}
                        contactEmail={null}
                        iconOnly
                      />
                    ) : null}
                    {isExpanded
                      ? <ChevronDown className="w-3.5 h-3.5 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />
                      : <ChevronRight className="w-3.5 h-3.5 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />}
                  </div>
                  {isExpanded && <ActionDetail row={row} />}
                </div>
              );
            }
            const e = r.entry;
            const { Icon, tileClass, sfdcCloudKind } = KIND_CONFIG[e.kind];
            return (
              <div key={r.key} className="border-b border-primary/40 dark:border-nightshade-800 last:border-b-0">
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={toggleRow}
                  onKeyDown={onRowKeyDown}
                  className="w-full flex gap-2 py-1.5 text-[12px] text-left items-start hover:bg-bg-secondary/60 dark:hover:bg-nightshade-800/60 rounded cursor-pointer"
                >
                  <span className="w-11 shrink-0 text-[11px] text-content-tertiary dark:text-nightshade-500 pt-1">{fmtDay(e.effectiveAt)}</span>
                  <span className={`w-[22px] h-[22px] shrink-0 rounded-md flex items-center justify-center ${tileClass}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <span className="min-w-0 flex-1 pt-px">
                    <span className="inline-flex items-center gap-1">
                      <span className="font-medium text-content-primary dark:text-nightshade-100">{entryTitle(e)}</span>
                      <SfdcCloudIcon kind={sfdcCloudKind} id={e.id} />
                      {(e.kind === 'task' || e.kind === 'event') && <SyncedRowsBadge entry={e} />}
                      {e.kind === 'task' && <ThreadCountBadge entry={e} />}
                    </span>
                    {entryMetaText(e) && (
                      <span className="text-content-tertiary dark:text-nightshade-500">
                        {' · '}{entryMetaText(e)}
                      </span>
                    )}
                  </span>
                  {isExpanded
                    ? <ChevronDown className="w-3.5 h-3.5 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />
                    : <ChevronRight className="w-3.5 h-3.5 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />}
                </div>
                {isExpanded && <EntryDetail entry={e} />}
              </div>
            );
          })}
        </div>
      ))}
      {mode !== 'action' && data.truncated && (
        <div className="text-[11px] text-content-tertiary dark:text-nightshade-500 pt-1 text-center">{ACCOUNT_TIMELINE_TRUNCATED_COPY}</div>
      )}
    </div>
  );
}
